import React, { useState, useEffect, useRef } from 'react';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import { Box } from "@mui/material";
import { toPng } from 'html-to-image';
import LinkShareModal from '../sharedComponents/LinkShareModal';
import MemeCard from '../sharedComponents/MemeCard';
import CopyReplyMenu from '../sharedComponents/CopyReplyMenu';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../../utils/slugify';
import { cardFunnelCopy } from '../../utils/funnelCopy';
import { track } from '../../utils/track';

const idiotTypeColors = {
  'Angry Idiot':    { bg: '#fce4ec', color: '#D7140F', border: '#ef9a9a' },
  'Curious Idiot':  { bg: '#e3f2fd', color: '#1748E8', border: '#90caf9' },
  'Hungry Idiot':   { bg: '#fff3e0', color: '#B84400', border: '#ffcc80' },
  'EV Advocate':    { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
};

export const IdioticCardBody = ({ idioticCardBodyProps }) => {
  const { title, titleBadge, answer, truthSnippet, truth, relatedChips, sources, expanded, idiotTypes, categories } = idioticCardBodyProps;

  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [downloading, setDownloading] = useState(false);
  const memeRef = useRef(null);

  useEffect(() => {
    const shareLink = `${window.location.origin}/s/${slugify(title)}.html`
    setShareLink(shareLink);
  }, [title]);

  const handleModalOpen = () => {
    track('myth_share_open', { title, placement: 'card_body' });
    setModalOpen(true);
  }

  const handleModalClose = () => {
    setModalOpen(false);
  }

  const handleDownloadMeme = async () => {
    if (!memeRef.current || downloading) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(memeRef.current, {
        cacheBust: true,
        pixelRatio: 1,
        width: 1080,
        height: 1080,
      });
      const link = document.createElement('a');
      const filename = `${slugify(title)}_meme.png`;
      link.download = filename;
      link.href = dataUrl;
      link.click();
      if (window.gtag) {
        window.gtag('event', 'download_meme', { title });
      }
    } catch (err) {
      console.error('Meme download failed', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleIdiotTypeClick = (cat, val) => {
    if(cat === 'idiotType'){
      navigate(`/persona/${val}`);
    }
  }

  return (
    <Collapse
        in={expanded}
        timeout={400}
        collapsedSize={72}
        easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        <CardContent
          sx={{ paddingTop: 0, paddingLeft: "20px", paddingRight: "20px", position: 'relative' }}
        >
          {!expanded && (
            <Box sx={{ position: 'relative', overflow: 'hidden', maxHeight: '52px' }}>
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                <strong>The Reality: </strong>
                {truthSnippet}
              </Typography>
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '28px',
                background: 'linear-gradient(transparent, white)',
              }} />
            </Box>
          )}
          {expanded && (
            <>
              {truth.map((paragraph, index) => (
                <Box
                  key={index}
                  sx={{ mb: index < truth.length - 1 ? 1.5 : 0 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", lineHeight: 1.7 }}
                  >
                    {index === 0 ? <strong>The Reality: </strong> : <></>}
                    {paragraph}
                  </Typography>
                </Box>
              ))}

              {relatedChips.map((chip, index) => {
                const colors = chip.cat === 'idiotType' ? idiotTypeColors[chip.val] : null;
                return (
                  <Chip
                    key={index}
                    label={chip.val}
                    variant={colors ? 'filled' : 'outlined'}
                    className='contentChips'
                    data-cta={chip.cat === 'idiotType' ? 'myth_card_persona_chip' : undefined}
                    data-cta-to={chip.cat === 'idiotType' ? `/persona/${chip.val}` : undefined}
                    onClick={() => handleIdiotTypeClick(chip.cat, chip.val)}
                    sx={colors ? {
                      backgroundColor: colors.bg,
                      color: colors.color,
                      border: `1px solid ${colors.border}`,
                      fontWeight: 600,
                      '&:hover': { backgroundColor: colors.border },
                    } : {
                      '&:hover': { backgroundColor: '#FFF6B8', borderColor: '#111111' },
                    }}
                  />
                );
              })}
              <Box sx={{ mt: 2, mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleModalOpen}
                  sx={{
                    backgroundColor: "#111111",
                    borderRadius: 0,
                    padding: "10px 24px",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    '&:hover': { backgroundColor: '#333333' },
                  }}
                >
                  <ShareIcon sx={{ marginRight: "8px", fontSize: "1.1rem" }} />
                  Share with an idiot
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDownloadMeme}
                  disabled={downloading}
                  sx={{
                    color: "#111111",
                    borderColor: "#111111",
                    borderRadius: 0,
                    padding: "10px 20px",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    '&:hover': { backgroundColor: '#FFF6B8', borderColor: '#333333' },
                  }}
                >
                  <DownloadIcon sx={{ marginRight: "8px", fontSize: "1.1rem" }} />
                  {downloading ? 'Generating...' : 'Download meme'}
                </Button>
                <CopyReplyMenu title={title} verdict={titleBadge} answer={answer} truthSnippet={truthSnippet} shareLink={shareLink} />
              </Box>
              <LinkShareModal link={shareLink} modalOpen={modalOpen} handleCloseModal={handleModalClose} idiotTypes={idiotTypes} />
              {/* Offscreen meme card used for PNG export */}
              <Box
                aria-hidden
                sx={{
                  position: 'fixed',
                  left: '-10000px',
                  top: 0,
                  pointerEvents: 'none',
                  opacity: 0,
                }}
              >
                <MemeCard ref={memeRef} title={title} truthSnippet={truthSnippet} />
              </Box>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e8e8e8' }}>
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, mb: 1 }}>
                  Sources
                </Typography>
                <Box data-track-context="myth_card_sources" sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {sources?.map((source, index) => (
                    <Chip
                      key={index}
                      label={source.name}
                      component="a"
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      size="small"
                      sx={{
                        borderRadius: 0,
                        color: "#111111",
                        border: '1px solid #111111',
                        backgroundColor: '#F4F4F4',
                        fontWeight: 500,
                        '&:hover': { backgroundColor: "#FFF6B8", borderColor: '#111111' },
                      }}
                    />
                  ))}
                </Box>
              </Box>
              {/* Contextual funnel close, every expanded card ends with a
                  next step toward /concierge (the dealer-intro money path). */}
              {(() => {
                const cta = cardFunnelCopy(categories);
                return (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#EAF5FC', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ color: '#111111', fontWeight: 600, flex: '1 1 260px' }}>
                      {cta.text}
                    </Typography>
                    <Button
                      onClick={() => { if (window.gtag) window.gtag('event', 'card_cta_concierge', { title }); navigate('/concierge'); }}
                      sx={{ backgroundColor: '#FFE600', color: '#111111', borderRadius: 0, px: 2.5, py: 0.75, fontWeight: 800, fontSize: '0.82rem', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#F5DC00' } }}
                    >
                      {cta.label}
                    </Button>
                  </Box>
                );
              })()}
            </>
          )}
        </CardContent>
      </Collapse>
  );
};
